import { Store } from "@ngxs/store";
import { catchError, filter, switchMap, take, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { interval, of } from "rxjs";
import { Router } from "@angular/router";

import { AuthenticationState } from "../authentication/state/authentication.state";

import { FetchDsConfiguration } from "./state/configuration.actions";
import { ConfigurationState, type ConfigurationStateModel } from "./state/configuration.state";

import type { AppConfig } from "./configuration.model";
import type { Subscription } from "rxjs";
import type { OnDestroy } from "@angular/core";

@Injectable({
	providedIn: "root",
})
export class ConfigurationService implements OnDestroy {
	private readonly CONFIG_URL = "assets/config/config.json";

	private appConfig: AppConfig | null = null;
	private screenRefreshSubscription: Subscription;
	private configIntervalSubscription: Subscription;

	public get config(): AppConfig | null {
		return this.appConfig;
	}

	public set config(appConfig: AppConfig | null) {
		this.appConfig = appConfig;
	}

	constructor(
		private store: Store,
		private router: Router,
	) {}

	ngOnDestroy(): void {
		if (this.configIntervalSubscription) {
			this.configIntervalSubscription.unsubscribe();
		}

		if (this.screenRefreshSubscription) {
			this.screenRefreshSubscription.unsubscribe();
		}
	}

	public getConfig(): AppConfig | null {
		const stateConfig = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		// Config may not be initialized yet
		if (!stateConfig) {
			return this.appConfig;
		}

		return {
			dsConfig: stateConfig.dsConfiguration,
			rootConfig: stateConfig.rootConfiguration,
		} as AppConfig;
	}

	public initializeScreenRefresh() {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		if (!configState.dsConfiguration) {
			return;
		}

		if (this.screenRefreshSubscription) {
			this.screenRefreshSubscription.unsubscribe();
		}

		const DEFAULT_REFRESH_INTERVAL = 5; // 5 minutes
		const MILLIS_PER_MIN = 60 * 1000;

		const screenReload = configState.dsConfiguration.screenReloadInterval || 0;
		const refreshMinutes = screenReload >= 1 ? screenReload : DEFAULT_REFRESH_INTERVAL;
		const refreshIntervalInMilis = refreshMinutes * MILLIS_PER_MIN;

		// Create an observable for the authentication state
		const authState$ = this.store.select(AuthenticationState.isRefreshingToken);
		const timer$ = interval(refreshIntervalInMilis);

		this.screenRefreshSubscription = timer$
			.pipe(
				switchMap(() => {
					// Also check the current URL to avoid reloading during login
					const currentUrl = this.router.url;
					const isAuthPage = currentUrl.includes("refresh-service-user-login");

					if (isAuthPage) {
						console.log("Skipping scheduled page reload: on authentication page");
						return of(null); // Skip this refresh cycle
					}
					// Get the current authentication state and handle refresh logic
					return authState$.pipe(
						// Wait until we're not refreshing a token
						filter((isRefreshing) => !isRefreshing),
						take(1), // Take only the first non-refreshing state
						tap(() => {
							console.log("Performing scheduled page reload");
							location.reload();
						}),
					);
				}),
			)
			.subscribe();
	}

	public initializeConfigPolling() {
		const configState = this.store.selectSnapshot<ConfigurationStateModel>(
			ConfigurationState.state,
		);

		if (!configState.dsConfiguration) {
			return;
		}

		if (this.configIntervalSubscription) {
			this.configIntervalSubscription.unsubscribe();
		}

		let configRefreshInterval = configState.dsConfiguration.configRefreshInterval * 1000 * 60;
		configRefreshInterval = configRefreshInterval >= 60000 ? configRefreshInterval : 300000;

		this.configIntervalSubscription = interval(configRefreshInterval)
			.pipe(
				switchMap(() => {
					return this.store.dispatch(
						new FetchDsConfiguration(configState.dsConfiguration?.resourceId, true),
					);
				}),
				catchError((e) => {
					console.error("Error while updating dsconfig.", e);
					return of(void 0);
				}),
				tap(() => {
					this.initializeConfigPolling();
				}),
			)
			.subscribe();
	}

	public async loadConfig(): Promise<AppConfig> {
		const url = `${this.CONFIG_URL}?nocache=${new Date().getTime()}`;
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		return (await response.json()) as AppConfig;
	}
}
